/**
 * /dashboard/people/$id/edit — edit an existing employee
 * (Phase 3c — 2026-06-01).
 *
 * Loads the employee from the HR-only directory (`useEmployees()`,
 * which calls GET /api/v1/employees) and renders the EditEmployeeForm.
 * The directory query is HR-admin only on the wire; a non-HR caller
 * 403s and the page surfaces a clean "no access" state.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/people/[id]/edit/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - next/link → @tanstack/react-router Link (typed)
 *
 * URL parity preserved: the Next.js route was /[id]/edit so the SPA
 * keeps /$id/edit (rather than collapsing to /$id) — any existing
 * deep-link survives the migration.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ShieldX } from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { useEmployees } from "@/hooks/use-hr";
import { toApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { EditEmployeeForm } from "@/components/people/edit-employee-form";

const CAN_EDIT_PEOPLE = new Set<Role>(["company_admin", "hr_manager"]);

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/people/$id/edit",
)({
  component: EditEmployeeRoute,
});

function EditEmployeeRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const { user } = useAuth();
  const allowed = !!user?.role && CAN_EDIT_PEOPLE.has(user.role);
  const q = useEmployees(allowed && validId);
  const employee = q.data?.employees.find((e) => e.id === numId);

  if (!allowed) {
    return (
      <>
        <PageHeader
          title="Edit employee"
          description="HR-admin only."
        />
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <ShieldX className="h-6 w-6" />
            </span>
            <p className="max-w-md text-sm text-muted-foreground">
              Only company admins and HR managers can edit employee records.
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to People
              </Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Edit employee"
        description={
          employee
            ? `${employee.first_name} ${employee.last_name}`
            : undefined
        }
      />

      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid employee id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid employee id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      )}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load employee</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && !employee && (
        <Alert variant="destructive">
          <AlertTitle>Employee not found</AlertTitle>
          <AlertDescription>
            This employee record doesn&apos;t exist (or isn&apos;t in
            your company).{" "}
            <Link
              to="/app/$tenantSlug/people" params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to People
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && employee && (
        <Card className="max-w-3xl">
          <CardContent className="pt-6">
            <EditEmployeeForm employee={employee} />
          </CardContent>
        </Card>
      )}
    </>
  );
}
